import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BillingInfo, UserBillingFullInfo } from './models'; // Ensure you import the correct types
import { PostSubscriptionRequest } from '../subscription/models';

interface SubscriptionInfo {
  credits: number;
  creditsLeft: number;
  creditsOut: number;
}

export type BillingDrawerType =
  | 'auto_payments'
  | 'add_payment_method'
  | 'payment_method'
  | 'billing_info'
  | 'enable_pro'
  | null;

interface BillingState {
  billingInfo: BillingInfo | null;
  billingInfoUser: UserBillingFullInfo | null;
  subscriptionInfo: SubscriptionInfo | null;
  showChangePlanModal: boolean;
  showBuyCreditsModal: boolean;

  flowMode: 'buy_credits' | 'add_card' | 'change_plan' | null;
  billingDrawerType: BillingDrawerType;

  creditsToAdd: number | null;
  subscriptionCtxPayload: PostSubscriptionRequest | null;

  subscriptionToPay: PostSubscriptionRequest | null;
}

const initialState: BillingState = {
  billingInfo: null,
  billingInfoUser: null,
  subscriptionInfo: null,
  showChangePlanModal: false,
  showBuyCreditsModal: false,
  flowMode: null,
  billingDrawerType: null,
  creditsToAdd: null,
  subscriptionCtxPayload: null,
  subscriptionToPay: null,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setBillingInfoUser(state, action: PayloadAction<UserBillingFullInfo | null>) {
      state.billingInfoUser = action.payload;
    },
    setBillingInfo(state, action: PayloadAction<BillingInfo>) {
      state.billingInfo = action.payload;
    },
    setSubscriptionInfo(state, action: PayloadAction<SubscriptionInfo>) {
      state.subscriptionInfo = action.payload;
    },
    setShowChangePlanModal(state, action: PayloadAction<boolean>) {
      state.showChangePlanModal = action.payload;
    },
    setShowBuyCreditsModal(state, action: PayloadAction<boolean>) {
      state.showBuyCreditsModal = action.payload;
    },
    setFlowMode(state, action: PayloadAction<'buy_credits' | 'add_card' | 'change_plan' | null>) {
      state.flowMode = action.payload;
    },
    resetFlowMode(state) {
      state.flowMode = null;
    },
    setBillingDrawerType(state, action: PayloadAction<BillingDrawerType>) {
      state.billingDrawerType = action.payload;
    },
    resetBillingDrawerType(state) {
      state.billingDrawerType = null;
    },
    setCreditsToAdd(state, action: PayloadAction<number | null>) {
      state.creditsToAdd = action.payload;
    },
    setSubscriptionCtxPayload(state, action: PayloadAction<PostSubscriptionRequest | null>) {
      state.subscriptionCtxPayload = action.payload;
    },
    resetTemporaryPaymentData(state) {
      state.creditsToAdd = null;
      state.subscriptionCtxPayload = null;
      state.billingDrawerType = null;
      state.flowMode = null;
    },
    setSubscriptionToPay(state, action: PayloadAction<PostSubscriptionRequest | null>) {
      state.subscriptionToPay = action.payload;
    },
  },
});

export const billingActions = billingSlice.actions;
export const billingSliceReducer = billingSlice.reducer;

export default billingSlice.reducer;
